<template>
    <div class="modal-overlay" @click.self="handleBackdropClick">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h2>{{ isEdit ? 'Edit Format' : 'New Format' }}</h2>
                <button class="close-button" @click="$emit('close')">&times;</button>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <div class="form-group name-field">
                        <label for="name">Name</label>
                        <input 
                            id="name"
                            v-model="formData.name"
                            type="text"
                            class="form-control"
                        >
                    </div>

                    <div class="form-group duration-field">
                        <label for="duration" class="nowrap">Duration (mins)</label>
                        <input 
                            id="duration"
                            v-model.number="formData.default_duration_minutes"
                            type="number"
                            min="1"
                            class="form-control"
                        >
                    </div>
                </div>

                <div class="form-group">
                    <label for="description">Description</label>
                    <textarea 
                        id="description"
                        v-model="formData.description"
                        class="form-control"
                        rows="3"
                    ></textarea>
                </div>

                <!-- Segments -->
                <div class="segments-section">
                    <h3>Segments</h3>
                    
                    <div class="segments-list">
                        <div v-for="(segment, index) in formData.segments" 
                             :key="index"
                             class="segment-item"
                        >
                            <div class="segment-header">
                                <span>Segment {{ index + 1 }}</span>
                                <div class="segment-actions">
                                    <button 
                                        v-if="index > 0"
                                        class="btn-icon move-btn" 
                                        @click="moveSegment(index, 'up')" 
                                        title="Move segment up"
                                    >
                                        <i class="material-icons">arrow_upward</i>
                                    </button>
                                    <button 
                                        v-if="index < formData.segments.length - 1"
                                        class="btn-icon move-btn" 
                                        @click="moveSegment(index, 'down')" 
                                        title="Move segment down"
                                    >
                                        <i class="material-icons">arrow_downward</i>
                                    </button>
                                    <button class="btn-icon add-btn" @click="addSegmentAfter(index)" title="Add segment below">
                                        <i class="material-icons">add</i>
                                    </button>
                                    <button class="btn-icon" @click="removeSegment(index)" title="Remove segment">
                                        <i class="material-icons">delete</i>
                                    </button>
                                </div>
                            </div>

                            <div class="segment-form">
                                <div class="segment-row">
                                    <div class="form-group flex-grow">
                                        <label>Name</label>
                                        <input 
                                            type="text" 
                                            v-model="segment.name"
                                            class="form-control"
                                        >
                                    </div>

                                    <div class="form-group duration-field">
                                        <label class="nowrap">Duration (mins)</label>
                                        <input 
                                            type="number" 
                                            v-model.number="segment.duration_minutes"
                                            min="1"
                                            class="form-control"
                                        >
                                    </div>

                                    <div class="form-group type-field">
                                        <label>Type</label>
                                        <select v-model="segment.segment_type" class="form-control">
                                            <option value="member_speaker">Member Speaker</option>
                                            <option value="external_speaker">External Speaker</option>
                                            <option value="discussion">Discussion</option>
                                            <option value="break">Break</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label>Description</label>
                                    <textarea 
                                        v-model="segment.description"
                                        class="form-control"
                                        rows="2"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <!-- Add first segment button when list is empty -->
                        <button v-if="!formData.segments.length" 
                                class="add-first-segment" 
                                @click="addSegment">
                            <i class="material-icons">add</i>
                            Add First Segment
                        </button>
                    </div>
                </div>

                <!-- Footer -->
                <div class="modal-footer">
                    <button class="btn-secondary" @click="$emit('close')" :disabled="loading">Cancel</button>
                    <button class="btn-primary" @click="save" :disabled="loading || !isValid">
                        <span v-if="loading" class="spinner"></span>
                        <span>{{ isEdit ? 'Save Changes' : 'Create Format' }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';

export default {
    name: 'FormatModal',
    props: {
        format: {
            type: Object,
            default: null
        },
        isEdit: {
            type: Boolean,
            default: false
        }
    },

    setup(props, { emit }) {
        const formData = ref({
            name: '',
            description: '',
            default_duration_minutes: 60,
            segments: []
        });
        const loading = ref(false);

        // Initialize form data if editing
        onMounted(() => {
            if (props.format) {
                formData.value = {
                    name: props.format.name,
                    description: props.format.description,
                    default_duration_minutes: props.format.default_duration_minutes,
                    segments: props.format.segments?.map(s => ({ ...s })) || []
                };
            }
        });

        const isValid = computed(() => {
            return formData.value.name &&
                   formData.value.default_duration_minutes > 0 &&
                   formData.value.segments.length > 0 &&
                   formData.value.segments.every(s => 
                       s.name && 
                       s.duration_minutes > 0 && 
                       s.segment_type
                   );
        });

        const addSegment = () => {
            formData.value.segments.push({
                name: '',
                duration_minutes: 30,
                order_index: formData.value.segments.length,
                segment_type: 'member_speaker',
                description: ''
            });
        };

        const addSegmentAfter = (index) => {
            formData.value.segments.splice(index + 1, 0, {
                name: '',
                duration_minutes: 30,
                order_index: index + 1,
                segment_type: 'member_speaker',
                description: ''
            });
            // Update order_index for remaining segments
            formData.value.segments.forEach((s, i) => s.order_index = i);
        };

        const removeSegment = (index) => {
            formData.value.segments.splice(index, 1);
            // Update order_index for remaining segments
            formData.value.segments.forEach((s, i) => s.order_index = i);
        };

        const moveSegment = (index, direction) => {
            if (direction === 'up') {
                const segment = formData.value.segments.splice(index, 1)[0];
                formData.value.segments.splice(index - 1, 0, segment);
            } else if (direction === 'down') {
                const segment = formData.value.segments.splice(index, 1)[0];
                formData.value.segments.splice(index + 1, 0, segment);
            }
            // Update order_index for remaining segments
            formData.value.segments.forEach((s, i) => s.order_index = i);
        };

        const handleBackdropClick = () => {
            emit('close');
        };

        const save = async () => {
            if (!isValid.value) return;
            
            try {
                loading.value = true;
                await emit('save', { ...formData.value });
            } catch (error) {
                console.error('Error saving format:', error);
            } finally {
                loading.value = false;
            }
        };

        return {
            formData,
            loading,
            isValid,
            addSegment,
            addSegmentAfter,
            removeSegment,
            moveSegment,
            handleBackdropClick,
            save
        };
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 0;
}

.modal-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-body {
    padding: 1.5rem;
}

.form-row {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 1rem;
}

.name-field {
    flex: 1;
    max-width: 50%;
}

.duration-field {
    width: 150px;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
    text-align: left;
}

.form-control {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
}

textarea.form-control {
    min-height: 80px;
    resize: vertical;
}

.segment-item {
    background: #f5f5f5;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
}

.segment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.segment-actions {
    display: flex;
    gap: 0.5rem;
}

.btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    color: #666;
}

.btn-icon:hover {
    background: rgba(0, 0, 0, 0.05);
}

.btn-icon.add-btn {
    color: #4CAF50;
}

.btn-icon.add-btn:hover {
    background: rgba(76, 175, 80, 0.1);
}

.add-first-segment {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 1rem;
    border: 2px dashed #ddd;
    border-radius: 8px;
    background: transparent;
    color: #666;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;
}

.add-first-segment:hover {
    border-color: #4CAF50;
    color: #4CAF50;
    background: rgba(76, 175, 80, 0.05);
}

.add-first-segment i {
    font-size: 20px;
}

.segment-form {
    background: white;
    border-radius: 4px;
    padding: 16px;
}

.segment-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: flex-end;
}

.flex-grow {
    flex: 1;
    min-width: 0;
}

.duration-field {
    width: 120px;
}

.type-field {
    width: 140px;
}

.modal-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e0e0e0;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
}

.btn-primary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    min-width: 100px;
    justify-content: center;
}

.btn-primary:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.btn-secondary {
    padding: 8px 16px;
    background: none;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    min-width: 80px;
}

.btn-secondary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinner {
    width: 16px;
    height: 16px;
    border: 2px solid #fff;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.nowrap {
    white-space: nowrap;
}

.btn-icon.move-btn {
    color: #2196F3;
}

.btn-icon.move-btn:hover {
    background: rgba(33, 150, 243, 0.1);
}

.btn-icon.move-btn:disabled {
    color: #ccc;
    cursor: default;
}

.btn-icon.move-btn:disabled:hover {
    background: transparent;
}

@media (max-width: 768px) {
    .form-row {
        flex-direction: column;
        gap: 1rem;
    }

    .name-field,
    .duration-field {
        width: 100%;
        max-width: 100%;
    }

    .segment-row {
        flex-direction: column;
        gap: 1rem;
    }

    .duration-field,
    .type-field {
        width: 100%;
    }
}
</style>
